<script setup lang="ts">
import { computed } from 'vue';
import { useQuasar } from 'quasar';
import type { PrintableCourseStudent } from '@/types/FlightSchoolStudent';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import AddressDisplay from '@/components/Utilities/AddressDisplay.vue';
import type { KnowledgeTestAgreementResponse } from '@/types/Response/FlightSchoolResponse';
import { useUserStore } from '@/stores/user';
import StudentSchoolSelect from '@/components/Student/Record/StudentSchoolSelect.vue';
import StudentExamDate from '@/components/Student/Record/StudentExamDate.vue';

const props = defineProps<{
    printableStudent: PrintableCourseStudent,
    currentCourse: FlightSchoolCourse,
    kta: KnowledgeTestAgreementResponse|undefined,
}>();

const ktaDate = computed(() => {
    const acceptedDateTime = props.kta?.acceptedDateTime;
    if (!acceptedDateTime) return '--';

    return new Date(acceptedDateTime).toLocaleDateString();
});

const $q = useQuasar();
const iconClasses = 'q-pr-sm q-pb-xs';
const profileRowClasses = 'row profile-row q-mb-md q-pb-md q-gutter-sm';

const userStore = useUserStore();
const { isGlobalAdmin } = userStore;
</script>
<template>
    <q-card
        class="student-info-card q-px-md"
        flat
        align="left"
    >
        <q-card-section>
            <div class="header text-weight-bold">
                Profile
            </div>
        </q-card-section>

        <q-card-section
            :horizontal="$q.screen.gt.sm"
            :class="{'row': !$q.screen.gt.sm}"
            class="card-body"
        >
            <q-card-section
                class="col-md col-12"
            >
                <div :class="profileRowClasses">
                    <div class="col-sm col-12">
                        <q-icon
                            :class="iconClasses"
                            name="sym_o_location_on"
                        />
                        <span class="label">Address:</span>
                    </div>
                    <div class="col-sm col-12">
                        <AddressDisplay :address="printableStudent.address" />
                    </div>
                </div>
                <div :class="profileRowClasses">
                    <div class="col-sm col-12">
                        <q-icon
                            :class="iconClasses"
                            name="sym_o_mail"
                        />
                        <span class="label">Email:</span>
                    </div>
                    <div
                        id="email"
                        class="col-sm col-12"
                    >
                        {{ printableStudent.email }}
                    </div>
                </div>
                <div :class="profileRowClasses">
                    <div class="col-sm col-12">
                        <q-icon
                            :class="iconClasses"
                            name="sym_o_call"
                        />
                        <span class="label">Day Phone:</span>
                    </div>
                    <div
                        id="day-phone"
                        class="col-sm col-12"
                    >
                        {{ printableStudent.phoneNumbers.day }}
                    </div>
                </div>
                <div :class="profileRowClasses">
                    <div class="col-sm col-12">
                        <q-icon
                            :class="iconClasses"
                            name="sym_o_call"
                        />
                        <span class="label">Evening Phone:</span>
                    </div>
                    <div class="col-sm col-12">
                        {{ printableStudent.phoneNumbers.evening ?? '--' }}
                    </div>
                </div>
            </q-card-section>
            <q-card-section class="col-md col-12">
                <div :class="profileRowClasses">
                    <div class="col-sm col-12">
                        <q-icon
                            :class="iconClasses"
                            name="sym_o_draw"
                        />
                        <span class="label">Knowledge Test Agreement:</span>
                    </div>
                    <div
                        id="kta"
                        class="col-sm col-12"
                    >
                        Signed on: {{ ktaDate }}
                    </div>
                </div>
                <div :class="profileRowClasses">
                    <div class="col-sm col-12">
                        <q-icon
                            :class="iconClasses"
                            name="sym_o_school"
                        />
                        <span class="label">Graduation Date:</span>
                    </div>
                    <div class="col-sm col-12">
                        {{ printableStudent.gradDateString }}
                    </div>
                </div>
                <StudentExamDate
                    :printable-student="printableStudent"
                />
                <div
                    v-if="isGlobalAdmin"
                    :class="profileRowClasses"
                >
                    <StudentSchoolSelect :student="printableStudent" />
                </div>
            </q-card-section>
        </q-card-section>
    </q-card>
</template>
<style scoped>
.header {
    font-size: 32px;
}
.card-body {
    font-size: 16px;
    color: var(--fsum-black);
    padding: 0;
}
:deep(.q-icon) {
    color: var(--fsum-gray2);
    font-size: 18px;
}
.profile-row {
    border-bottom: 1px solid var(--fsum-gray5);
}
span.label {
    font-weight: bold;
}
div#email {
    overflow-wrap: break-word;
}
</style>
